import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Edit2, Trash2, FileText, X, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: "new_lead", label: "New Lead Follow-up" },
  { value: "open_house", label: "Open House Invite" },
  { value: "price_drop", label: "Price Drop Alert" },
  { value: "general", label: "General Follow-up" },
  { value: "other", label: "Other" },
];

const MERGE_FIELDS = ["{first_name}", "{last_name}", "{agent_name}", "{address}"];

function TemplateModal({ template, onSave, onClose }) {
  const [name, setName] = useState(template?.name || "");
  const [category, setCategory] = useState(template?.category || "general");
  const [body, setBody] = useState(template?.body || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim() || !body.trim()) { alert("Name and message are required"); return; }
    setSaving(true);
    try {
      if (template?.id) {
        await axios.put(`${API}/api/templates/${template.id}`, { name, category, body });
      } else {
        await axios.post(`${API}/api/templates`, { name, category, body });
      }
      onSave();
    } catch (e) { alert("Save failed"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="template-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold font-heading text-slate-900">{template?.id ? "Edit Template" : "New Template"}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Template Name</label>
            <input value={name} onChange={e => setName(e.target.value)} className="input" placeholder="e.g. Price Drop Alert" data-testid="template-name-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Category</label>
            <select value={category} onChange={e => setCategory(e.target.value)} className="input" data-testid="template-category-select">
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium text-slate-700">Message</label>
              <div className="flex flex-wrap gap-1">
                {MERGE_FIELDS.map(f => (
                  <button key={f} type="button" onClick={() => setBody(b => b + f)} className="text-xs px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded hover:bg-[#FFF0EB] hover:text-[#E84E29] transition-colors">{f}</button>
                ))}
              </div>
            </div>
            <textarea value={body} onChange={e => setBody(e.target.value)} className="input resize-none" rows={4} placeholder="Write your template message..." data-testid="template-body-input" />
            <p className="text-xs text-slate-400 mt-1">{body.length} characters</p>
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button onClick={onClose} className="btn-secondary" data-testid="cancel-template-btn">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-primary" data-testid="save-template-btn">
              {saving ? "Saving..." : <><Check size={14} /> Save Template</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | {} | {template}

  const load = () => {
    setLoading(true);
    axios.get(`${API}/api/templates`)
      .then(r => setTemplates(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const deleteTemplate = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    await axios.delete(`${API}/api/templates/${id}`);
    load();
  };

  const grouped = CATEGORIES.reduce((acc, cat) => {
    acc[cat.value] = templates.filter(t => t.category === cat.value);
    return acc;
  }, {});

  return (
    <div className="p-4 md:p-8" data-testid="templates-page">
      {modal !== null && (
        <TemplateModal
          template={modal}
          onSave={() => { setModal(null); load(); }}
          onClose={() => setModal(null)}
        />
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold font-heading text-slate-900">Templates</h1>
          <p className="text-slate-500 text-sm mt-0.5">{templates.length} saved message templates</p>
        </div>
        <button onClick={() => setModal({})} className="btn-primary" data-testid="new-template-btn">
          <Plus size={15} /> New Template
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#FF5C35] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="space-y-8">
          {CATEGORIES.map(cat => {
            const items = grouped[cat.value] || [];
            if (items.length === 0) return null;
            return (
              <div key={cat.value} data-testid={`template-category-${cat.value}`}>
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">{cat.label}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {items.map(t => (
                    <div key={t.id} className="card p-5 hover:shadow-md transition-all duration-200" data-testid={`template-card-${t.id}`}>
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="flex items-start gap-2.5">
                          <FileText size={15} className="text-[#FF5C35] mt-0.5 shrink-0" />
                          <p className="font-medium text-slate-900 text-sm">{t.name}</p>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <button onClick={() => setModal(t)} className="p-2 text-slate-400 hover:text-[#FF5C35] hover:bg-[#FFF0EB] rounded transition-colors" data-testid={`edit-template-${t.id}`}>
                            <Edit2 size={15} />
                          </button>
                          <button onClick={() => deleteTemplate(t.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors" data-testid={`delete-template-${t.id}`}>
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </div>
                      <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">{t.body}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {templates.length === 0 && (
            <div className="card p-12 text-center" data-testid="empty-templates">
              <FileText size={32} className="text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 font-medium">No templates yet</p>
              <p className="text-slate-400 text-sm mt-1">Create reusable message templates</p>
              <button onClick={() => setModal({})} className="btn-primary mt-4">
                <Plus size={15} /> Create Template
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
